import { useCallback, useContext, useEffect, useState } from 'react';
import {
    Alert, Box, Breadcrumbs, FormControlLabel, IconButton, Menu as ActionMenu, MenuItem, Pagination, Switch, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import RadiusButton from '../../../../components/RadiusButton';
import { useToaster } from '../../../../providers/ToasterProvider';
import { ProjectContext } from '../../../../providers/projectProvider';
import {
    changeVenueActiveStatus, downloadVenueTemplate, getLocations, getVenueListByPage,
} from '../../../../services/venueManagementService';
import { VenueFormModal } from './VenueFormModal';
import { LocationModal } from './LocationModal';
import { BulkUploadCard } from './BulkUploadCard';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, UPLOAD_TYPES } from './venueManagementConstants';

import SEARCH_ICON from '../../../../assets/images/search-icon.svg';
import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ADD_USER from '../../../../assets/images/add-user.svg';

import './VenueManagement.scss';

const columns = [
    { id: 'venueName', label: 'Venue name', width: '19%' },
    { id: 'venueCode', label: 'Code', width: '12%' },
    { id: 'cityName', label: 'City', width: '10%' },
    { id: 'stateName', label: 'State', width: '10%' },
    { id: 'maxCount', label: 'Count', align: 'right', width: '8%' },
    { id: 'serverManagerName', label: 'SMN', title: 'Server Manager Name', width: '12%' },
    { id: 'contactNumber', label: 'Contact', width: '12%' },
    { id: 'status', label: 'Status', width: '112px' },
    { id: 'actions', label: 'Action', align: 'right', width: '64px' },
];

const SEARCH_DELAY_MS = 400;

const formatCount = (value) => Number(value ?? 0).toLocaleString('en-IN');

const LOCATION_TILES = [
    {
        type: 'country', label: '+ Country', countKey: 'countries', requires: null,
        icon: <><circle cx='12' cy='12' r='9' /><path d='M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18' /></>,
    },
    {
        type: 'state', label: '+ State', countKey: 'states', requires: 'countries', disabledTitle: 'Add a country first',
        icon: <><path d='M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2z' /><path d='M9 4v14M15 6v14' /></>,
    },
    {
        type: 'city', label: '+ City', countKey: 'cities', requires: 'states', disabledTitle: 'Add a state first',
        icon: <path d='M3 21h18M5 21V9l5-3v15M10 21V4l9 4v13M14 10h2M14 14h2M7 12h1M7 16h1' />,
    },
];

// Countries, states, cities and venues on one screen: venues list on the left, locations,
// bulk upload and format downloads on the right.
export const VenueManagement = () => {

    const { projectDetails } = useContext(ProjectContext);
    const { addNotification } = useToaster();

    const [locations, setLocations] = useState({ countries: [], states: [], cities: [] });
    const [venues, setVenues] = useState({ rows: [], count: 0, totalCapacity: 0 });
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [venuesVersion, setVenuesVersion] = useState(0);

    const [venueModal, setVenueModal] = useState({ open: false, venue: null });
    const [locationModal, setLocationModal] = useState({ open: false, type: 'country' });
    const [rowMenu, setRowMenu] = useState({ anchorEl: null, venue: null });
    const [uploadType, setUploadType] = useState('master');


    const maxPages = Math.max(Math.ceil(venues.count / pageSize), 1);
    const hasCities = locations.cities.length > 0;

    const loadLocations = useCallback(async () => {
        try {
            const res = await getLocations();
            if (res.code === 'OK') setLocations(res.data);
        } catch (error) {
            console.log('There is an error while fetching locations', error);
        }
    }, []);

    useEffect(() => {
        loadLocations();
    }, [loadLocations]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search.trim());
            setCurrentPage(1);
        }, SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        // Only the latest request may update the list: an older, slower one must not overwrite it.
        let isCurrent = true;
        const fetchVenues = async () => {
            try {
                const res = await getVenueListByPage({ search: appliedSearch, stateId: stateFilter, page: currentPage, limit: pageSize });
                if (isCurrent && res.code === 'OK') setVenues(res.data);
            } catch (error) {
                console.log('There is an error while fetching venues', error);
            }
        }
        fetchVenues();
        return () => { isCurrent = false; };
    }, [appliedSearch, stateFilter, currentPage, pageSize, venuesVersion]);

    // After a delete or a smaller page size the current page can fall past the last one.
    useEffect(() => {
        if (currentPage > maxPages) setCurrentPage(maxPages);
    }, [currentPage, maxPages]);

    const refreshAll = () => {
        loadLocations();
        setVenuesVersion((version) => version + 1);
    }

    const closeRowMenu = () => {
        setRowMenu({ anchorEl: null, venue: null });
    }

    const onEditVenue = () => {
        const venue = rowMenu.venue;
        closeRowMenu();
        setVenueModal({ open: true, venue });
    }

    // Venues are enabled or disabled, never deleted.
    const onActiveToggle = async (venue, isActive) => {
        try {
            const res = await changeVenueActiveStatus(venue.id, { isActive });
            if (res.code === 'OK') {
                setVenues((current) => ({
                    ...current,
                    rows: current.rows.map((row) => row.id === venue.id ? { ...row, isActive } : row),
                }));
                addNotification(`${venue.venueName} ${isActive ? 'enabled' : 'disabled'}.`, 'success');
            }
        } catch (error) {
            addNotification(error?.response?.data?.message ?? 'Venue status could not be changed.', 'danger');
            console.log('There is an error while changing venue status', error);
        }
    }

    const onDownloadFormat = async (uploadTypeInfo) => {
        try {
            const res = await downloadVenueTemplate(uploadTypeInfo.key);
            const url = window.URL.createObjectURL(res);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', uploadTypeInfo.templateFile);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            addNotification('File downloaded successfully.', 'success');
        } catch (error) {
            addNotification(error?.response?.data?.message ?? 'Format could not be downloaded.', 'danger');
            console.log('There is an error while downloading venue format', error);
        }
    }

    const renderTableCell = (columnId, row) => {
        switch (columnId) {
            case 'venueName':
                return <span className='cell-ellipsis cell-strong' title={row.venueName}>{row.venueName}</span>;
            // Venues created before Venue Management have no code or max count yet.
            case 'venueCode':
                return row.venueCode ? <span className='venue-code-chip'>{row.venueCode}</span> : '—';
            case 'maxCount':
                return row.maxCount ? formatCount(row.maxCount) : '—';
            case 'status':
                return (
                    <FormControlLabel
                        sx={{ m: 0 }}
                        control={<Switch size='small' checked={row.isActive} onChange={(e, value) => onActiveToggle(row, value)} />}
                        label={<Typography variant='body2'>{row.isActive ? 'Active' : 'Disabled'}</Typography>}
                    />
                );
            case 'actions':
                return (
                    <IconButton size='small' aria-haspopup='menu' aria-label={`Actions for ${row.venueName}`}
                        onClick={(e) => setRowMenu({ anchorEl: e.currentTarget, venue: row })}>
                        <svg width='16' height='16' viewBox='0 0 24 24' fill='currentColor'>
                            <circle cx='12' cy='5' r='1.8' /><circle cx='12' cy='12' r='1.8' /><circle cx='12' cy='19' r='1.8' />
                        </svg>
                    </IconButton>
                );
            default:
                return <span className='cell-ellipsis' title={row[columnId] || ''}>{row[columnId] || '—'}</span>;
        }
    }

    const sortedStates = [...locations.states].sort((a, b) => a.name.localeCompare(b.name));
    const firstRow = venues.count ? (currentPage - 1) * pageSize + 1 : 0;
    const lastRow = Math.min(venues.count, currentPage * pageSize);

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label='breadcrumb' separator='' sx={{ gap: 12 }}>
                    <Typography color='text.primary' sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color='text.primary' sx={{ fontWeight: 500 }}>Venue Management</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{ display: 'flex', flexDirection: 'column' }}>
                <h1 className='title'>
                    Venue Management
                </h1>

                <Box className='venue-management'>
                    <Box className='venue-card venues-card'>
                        <Box className='venues-toolbar'>
                            <Box className='venue-stat'>
                                <span className='venue-stat-label'>Venues</span>
                                <span className='venue-stat-value'>{formatCount(venues.count)}</span>
                            </Box>
                            <Box className='venue-stat venue-stat--primary'>
                                <span className='venue-stat-label'>Capacity</span>
                                <span className='venue-stat-value'>{formatCount(venues.totalCapacity)}</span>
                            </Box>
                            <span className='venues-toolbar-grow' />
                            <div className='search-wrapper venue-search'>
                                <input type='text' className='search-box' value={search} onChange={(e) => setSearch(e.target.value)}
                                    placeholder='Search...' title='Search by venue name, code, city or state' aria-label='Search venues' />
                                <img className='search-icon' src={SEARCH_ICON} alt='' />
                            </div>
                            <TextField select size='small' label='State' value={stateFilter} sx={{ minWidth: 160 }}
                                onChange={(e) => { setStateFilter(e.target.value); setCurrentPage(1); }}>
                                <MenuItem value=''>All states</MenuItem>
                                {sortedStates.map((state) => <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>)}
                            </TextField>
                            <RadiusButton sx={{ textTransform: 'uppercase', fontWeight: 500 }}
                                color='secondary'
                                variant='contained'
                                startIcon={<img src={ADD_USER} alt='' />}
                                disabled={!hasCities}
                                onClick={() => setVenueModal({ open: true, venue: null })}>
                                Add Venue
                            </RadiusButton>
                        </Box>

                        {!hasCities && <Alert severity='info' sx={{ py: 0 }}>Add a country, state and city first.</Alert>}

                        <TableContainer className={`venues-table${hasCities ? '' : ' venues-table--locked'}`}>
                            <Table stickyHeader size='small' aria-label='venue table'>
                                <TableHead>
                                    <TableRow>
                                        {columns.map((column) => (
                                            <TableCell key={column.id} align={column.align ?? 'left'} sx={{ width: column.width }} title={column.title ?? column.label}>{column.label}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {!venues.rows.length
                                        ? <TableRow>
                                            <TableCell colSpan={columns.length} className='venues-empty'>
                                                {appliedSearch || stateFilter ? 'No matches.' : 'No venues yet.'}
                                            </TableCell>
                                        </TableRow>
                                        : venues.rows.map((row) => (
                                            <TableRow hover key={row.id} className={row.isActive ? '' : 'venue-row--disabled'}>
                                                {columns.map((column) => (
                                                    <TableCell key={column.id} align={column.align ?? 'left'}
                                                        className={column.id === 'stateName' || column.id === 'serverManagerName' || column.id === 'contactNumber' ? 'cell-muted' : ''}>
                                                        {renderTableCell(column.id, row)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {venues.count > 0 && (
                            <Box className='venues-pager'>
                                <Typography variant='body2' color='text.secondary'>{firstRow}–{lastRow} of {formatCount(venues.count)}</Typography>
                                <TextField select size='small' label='Rows' value={pageSize} sx={{ ml: 'auto', minWidth: 90 }}
                                    onChange={(e) => { setPageSize(e.target.value); setCurrentPage(1); }}>
                                    {PAGE_SIZE_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>{option}</MenuItem>
                                    ))}
                                </TextField>
                                <Pagination count={maxPages} page={currentPage} onChange={(e, value) => setCurrentPage(value)} shape='rounded' />
                            </Box>
                        )}
                    </Box>

                    <Box className='venue-side'>
                        <Box className='venue-card'>
                            <Typography component='h3' className='venue-card-title'>Locations</Typography>
                            <Box className='location-tiles'>
                                {LOCATION_TILES.map((tile) => {
                                    const isDisabled = Boolean(tile.requires) && !locations[tile.requires].length;
                                    return (
                                        <button key={tile.type} type='button' className='location-tile' disabled={isDisabled}
                                            title={isDisabled ? tile.disabledTitle : ''} aria-label={`Add ${tile.type}`}
                                            onClick={() => setLocationModal({ open: true, type: tile.type })}>
                                            <span className='location-tile-icon'>
                                                <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>{tile.icon}</svg>
                                            </span>
                                            <span className='location-tile-count'>{locations[tile.countKey].length}</span>
                                            <span className='location-tile-label'>{tile.label}</span>
                                        </button>
                                    );
                                })}
                            </Box>
                        </Box>

                        <BulkUploadCard
                            uploadType={uploadType}
                            onUploadTypeChange={setUploadType}
                            locations={locations}
                            onUploaded={refreshAll}
                        />

                        <Box className='venue-card'>
                            <Typography component='h3' className='venue-card-title'>Download formats</Typography>
                            <Box className='format-tiles'>
                                {UPLOAD_TYPES.map((item) => (
                                    <button key={item.key} type='button' className={`format-tile${item.key === 'master' ? ' format-tile--master' : ''}`}
                                        aria-label={`Download ${item.label} format`} onClick={() => onDownloadFormat(item)}>
                                        <span className='format-tile-icon'>
                                            <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round'>
                                                <path d='M12 3v12m0 0-4-4m4 4 4-4' /><path d='M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4' />
                                            </svg>
                                        </span>
                                        <span className='format-tile-label'>{item.label}</span>
                                        <span className='format-tile-ext'>.xlsx</span>
                                    </button>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                <ActionMenu anchorEl={rowMenu.anchorEl} open={Boolean(rowMenu.anchorEl)} onClose={closeRowMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <MenuItem onClick={onEditVenue}>Edit</MenuItem>
                </ActionMenu>

                <VenueFormModal
                    open={venueModal.open}
                    venue={venueModal.venue}
                    locations={locations}
                    defaultStateId={locations.states.find((state) => state.id === stateFilter && state.cityCount > 0)?.id ?? ''}
                    onClose={() => setVenueModal({ open: false, venue: null })}
                    onSaved={() => { setVenueModal({ open: false, venue: null }); refreshAll(); }}
                />

                <LocationModal
                    open={locationModal.open}
                    type={locationModal.type}
                    locations={locations}
                    onClose={() => setLocationModal((modal) => ({ ...modal, open: false }))}
                    onChanged={refreshAll}
                />
            </Container>
        </MainContainer>
    );
}
